import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import API from '../api';
import { logoutUser } from '../api';
import NewCustomerModal from './NewCustomerModal';
import EditCustomerModal from './EditCustomerModal';

export default function Dashboard() {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [columns, setColumns] = useState({
    created_at: false,
    customer_number: true,
    company_name: true,
    main_contact: true,
    main_line_phone: true,
  });

  const [showDirectDispatch, setShowDirectDispatch] = useState(false);
  const [showFax, setShowFax] = useState(false);
  const [showPbx, setShowPbx] = useState(false);
  const [showWanCircuits, setShowWanCircuits] = useState(false);
  const [showOpensips, setShowOpensips] = useState(false);

  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return `${year}-${month}-${day} ${hours}:${minutes} ${tz}`;
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert("You must be logged in.");
      navigate('/');
    }
  }, [navigate]);

  const fetchCustomers = async () => {
    try {
      const res = await API.get('/customers');
      setCustomers(res.data);
      setFilteredCustomers(res.data);
    } catch (err) {
      console.error("Error fetching customers:", err);
      if (err.response?.status === 401) {
        alert('Session expired. Please log in again.');
        navigate('/');
      } else {
        alert('Error loading customers');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await API.delete(`/customers/${id}`);
      fetchCustomers();
      setSuccessMessage('Customer has been successfully deleted.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch {
      alert('Failed to delete customer');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setShowEditModal(true);
  };

  const handleLogout = () => {
  logoutUser(); // This handles everything
 };

  const exportToCSV = () => {
    const header = [
      'created_at',
      ...Object.keys(columns).filter(col => columns[col] && col !== 'created_at'),
      ...(showDirectDispatch ? ['address_for_field_engineer', 'onsite_contact', 'regular_business_hours', 'emergency_business_hours'] : []),
      ...(showFax ? ['fax_col1', 'fax_col2', 'fax_col3', 'fax_col4', 'fax_col5'] : []),
      ...(showPbx ? ['pbx_col1', 'pbx_col2', 'pbx_col3', 'pbx_col4', 'pbx_col5'] : []),
      ...(showWanCircuits ? ['wan_col1', 'wan_col2', 'wan_col3', 'wan_col4', 'wan_col5'] : []),
      ...(showOpensips ? ['opensips_col1', 'opensips_col2', 'opensips_col3', 'opensips_col4', 'opensips_col5'] : []),
    ];

    const rows = visibleCustomers.map(p =>
      header.map(h => {
        if (h === 'created_at' && p[h]) {
          return `"${formatTimestamp(p[h])}"`;
        }
        return `"${p[h] ?? ''}"`;
      }).join(',')
    );

    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customers.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleColumn = (col) => {
    setColumns(prev => ({ ...prev, [col]: !prev[col] }));
  };

  const toggleDirectDispatch = () => setShowDirectDispatch(!showDirectDispatch);
  const toggleFax = () => setShowFax(!showFax);
  const togglePbx = () => setShowPbx(!showPbx);
  const toggleWanCircuits = () => setShowWanCircuits(!showWanCircuits);
  const toggleOpensips = () => setShowOpensips(!showOpensips);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setCurrentPage(1);
    if (query === '') {
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter(customer =>
        Object.values(customer).some(value =>
          value && value.toString().toLowerCase().includes(query)
        )
      );
      setFilteredCustomers(filtered);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
    const sorted = [...filteredCustomers].sort((a, b) => {
      const valA = a[key] ?? '';
      const valB = b[key] ?? '';
      if (key === 'created_at') {
        const dateA = new Date(valA).getTime() || 0;
        const dateB = new Date(valB).getTime() || 0;
        return direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      if (['customer_number', 'main_line_phone'].includes(key)) {
        const numA = Number(valA) || 0;
        const numB = Number(valB) || 0;
        return direction === 'asc' ? numA - numB : numB - numA;
      }
      const strA = valA.toString().toLowerCase();
      const strB = valB.toString().toLowerCase();
      return direction === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
    });
    setFilteredCustomers(sorted);
  };

  const getSortIndicator = (key) => {
    const isActive = sortConfig.key === key;
    return (
      <span className="inline-flex items-center ml-1 text-gray-400">
        <span className={isActive && sortConfig.direction === 'asc' ? 'text-blue-600' : ''}>▲</span>
        <span className="mx-1">/</span>
        <span className={isActive && sortConfig.direction === 'desc' ? 'text-blue-600' : ''}>▼</span>
      </span>
    );
  };

  const customersPerPage = 10;
  const indexOfLast = currentPage * customersPerPage;
  const indexOfFirst = indexOfLast - customersPerPage;
  const visibleCustomers = filteredCustomers.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredCustomers.length / customersPerPage);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (location.search.includes('refresh=true')) {
      fetchCustomers();
      navigate('/dashboard', { replace: true });
    }
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 to-blue-200 p-6">
      <div className="w-[80%] mx-auto bg-white p-8 rounded-xl shadow-xl">
        {successMessage && (
          <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded shadow text-center font-semibold">
            {successMessage}
          </div>
        )}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-blue-700">Customers Tailored for Tier 2</h2>
          <div className="space-x-2">
            <button
              onClick={exportToCSV}
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg shadow-md transition"
            >
              Export CSV
            </button>
            <Link
              to="/bulk-upload?redirect=/dashboard"
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-4 py-2 rounded-lg shadow-md transition"
            >
              Bulk Upload Customers
            </Link>
            <Link
              to="/history"
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-4 py-2 rounded-lg shadow-md transition"
            >
              View Change History
            </Link>
            <button
              onClick={() => setShowNewModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg shadow-md transition"
            >
              + New Customer
            </button>
            <button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg shadow-md transition"
            >
              Logout
            </button>
          </div>
        </div>
        <div className="mb-4">
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Search customers..."
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="mb-4">
          <strong className="text-gray-700">Toggle Columns:</strong>
          <div className="flex flex-wrap gap-2 mt-2">
            {Object.keys(columns).map(col => (
              <label key={col} className="inline-flex items-center">
                <input
                  type="checkbox"
                  checked={columns[col]}
                  onChange={() => toggleColumn(col)}
                  className="mr-1"
                />
                <span className="text-sm text-gray-700">{col.replace(/_/g, ' ')}</span>
              </label>
            ))}
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={showDirectDispatch}
                onChange={toggleDirectDispatch}
                className="mr-1"
              />
              <span className="text-sm text-gray-700">Direct Dispatch</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={showFax}
                onChange={toggleFax}
                className="mr-1"
              />
              <span className="text-sm text-gray-700">Fax</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={showPbx}
                onChange={togglePbx}
                className="mr-1"
              />
              <span className="text-sm text-gray-700">PBX</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={showWanCircuits}
                onChange={toggleWanCircuits}
                className="mr-1"
              />
              <span className="text-sm text-gray-700">WAN Circuits</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                checked={showOpensips}
                onChange={toggleOpensips}
                className="mr-1"
              />
              <span className="text-sm text-gray-700">OpenSIPS</span>
            </label>
          </div>
        </div>
        {filteredCustomers.length === 0 ? (
          <p className="text-center text-gray-700 text-lg">
            {searchQuery ? 'No customers match your search.' : 'Welcome! You have no customers yet.'}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto border-collapse border border-gray-300 text-sm whitespace-nowrap">
                <thead className="bg-blue-100 text-gray-700">
                  <tr>
                    <th className="border border-gray-300 px-4 py-2">Actions</th>
                    {columns.created_at && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('created_at')}>
                        Created At{getSortIndicator('created_at')}
                      </th>
                    )}
                    {columns.customer_number && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('customer_number')}>
                        Customer Number{getSortIndicator('customer_number')}
                      </th>
                    )}
                    {columns.company_name && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('company_name')}>
                        Company Name{getSortIndicator('company_name')}
                      </th>
                    )}
                    {columns.main_contact && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('main_contact')}>
                        Main Contact{getSortIndicator('main_contact')}
                      </th>
                    )}
                    {columns.main_line_phone && (
                      <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('main_line_phone')}>
                        Main Line Phone Number{getSortIndicator('main_line_phone')}
                      </th>
                    )}
                    {showDirectDispatch && (
                      <>
                        <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('address_for_field_engineer')}>
                          Address for Field Engineer{getSortIndicator('address_for_field_engineer')}
                        </th>
                        <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('onsite_contact')}>
                          On-Site Contact{getSortIndicator('onsite_contact')}
                        </th>
                        <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('regular_business_hours')}>
                          Regular Business Hours{getSortIndicator('regular_business_hours')}
                        </th>
                        <th className="border border-gray-300 px-4 py-2 cursor-pointer hover:bg-blue-200" onClick={() => handleSort('emergency_business_hours')}>
                          Emergency Business Hours{getSortIndicator('emergency_business_hours')}
                        </th>
                      </>
                    )}
                    {showFax && (
                      <>
                        <th className="border border-gray-300 px-4 py-2">Fax Col1</th>
                        <th className="border border-gray-300 px-4 py-2">Fax Col2</th>
                        <th className="border border-gray-300 px-4 py-2">Fax Col3</th>
                        <th className="border border-gray-300 px-4 py-2">Fax Col4</th>
                        <th className="border border-gray-300 px-4 py-2">Fax Col5</th>
                      </>
                    )}
                    {showPbx && (
                      <>
                        <th className="border border-gray-300 px-4 py-2">PBX Col1</th>
                        <th className="border border-gray-300 px-4 py-2">PBX Col2</th>
                        <th className="border border-gray-300 px-4 py-2">PBX Col3</th>
                        <th className="border border-gray-300 px-4 py-2">PBX Col4</th>
                        <th className="border border-gray-300 px-4 py-2">PBX Col5</th>
                      </>
                    )}
                    {showWanCircuits && (
                      <>
                        <th className="border border-gray-300 px-4 py-2">WAN Col1</th>
                        <th className="border border-gray-300 px-4 py-2">WAN Col2</th>
                        <th className="border border-gray-300 px-4 py-2">WAN Col3</th>
                        <th className="border border-gray-300 px-4 py-2">WAN Col4</th>
                        <th className="border border-gray-300 px-4 py-2">WAN Col5</th>
                      </>
                    )}
                    {showOpensips && (
                      <>
                        <th className="border border-gray-300 px-4 py-2">OpenSIPS Col1</th>
                        <th className="border border-gray-300 px-4 py-2">OpenSIPS Col2</th>
                        <th className="border border-gray-300 px-4 py-2">OpenSIPS Col3</th>
                        <th className="border border-gray-300 px-4 py-2">OpenSIPS Col4</th>
                        <th className="border border-gray-300 px-4 py-2">OpenSIPS Col5</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {visibleCustomers.map(p => (
                    <tr key={p.id} className="hover:bg-gray-100">
                      <td className="border border-gray-200 px-4 py-2 text-center space-x-2">
                        <button
                          onClick={() => handleEdit(p)}
                          className="bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded"
                        >
                          Delete
                        </button>
                        <button
                          onClick={() => navigate(`/history/${p.id}`)}
                          className="bg-indigo-500 hover:bg-indigo-600 text-white px-2 py-1 rounded"
                        >
                          View History
                        </button>
                      </td>
                      {columns.created_at && (
                        <td className="border border-gray-200 px-4 py-2">
                          {formatTimestamp(p.created_at)}
                        </td>
                      )}
                      {columns.customer_number && <td className="border border-gray-200 px-4 py-2">{p.customer_number}</td>}
                      {columns.company_name && <td className="border border-gray-200 px-4 py-2">{p.company_name}</td>}
                      {columns.main_contact && <td className="border border-gray-200 px-4 py-2">{p.main_contact}</td>}
                      {columns.main_line_phone && <td className="border border-gray-200 px-4 py-2">{p.main_line_phone}</td>}
                      {showDirectDispatch && (
                        <>
                          <td className="border border-gray-200 px-4 py-2">{p.address_for_field_engineer}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.onsite_contact}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.regular_business_hours}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.emergency_business_hours}</td>
                        </>
                      )}
                      {showFax && (
                        <>
                          <td className="border border-gray-200 px-4 py-2">{p.fax_col1}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.fax_col2}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.fax_col3}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.fax_col4}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.fax_col5}</td>
                        </>
                      )}
                      {showPbx && (
                        <>
                          <td className="border border-gray-200 px-4 py-2">{p.pbx_col1}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.pbx_col2}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.pbx_col3}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.pbx_col4}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.pbx_col5}</td>
                        </>
                      )}
                      {showWanCircuits && (
                        <>
                          <td className="border border-gray-200 px-4 py-2">{p.wan_col1}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.wan_col2}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.wan_col3}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.wan_col4}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.wan_col5}</td>
                        </>
                      )}
                      {showOpensips && (
                        <>
                          <td className="border border-gray-200 px-4 py-2">{p.opensips_col1}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.opensips_col2}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.opensips_col3}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.opensips_col4}</td>
                          <td className="border border-gray-200 px-4 py-2">{p.opensips_col5}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-center mt-4 space-x-2">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`px-3 py-1 rounded ${
                    currentPage === i + 1
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <div className="text-center mt-4">
              <span className="inline-block px-4 py-2 bg-gray-100 text-gray-800 rounded-full shadow-sm text-sm">
                Showing{' '}
                <span className="font-medium">{indexOfFirst + 1}</span> to{' '}
                <span className="font-medium">{Math.min(indexOfLast, filteredCustomers.length)}</span>{' '}
                from a total of {' '}
                <span className="font-semibold">{filteredCustomers.length}</span>{' '}
                {filteredCustomers.length === 1 ? 'customer' : 'customers'}
              </span>
            </div>
          </>
        )}
        {showNewModal && (
          <NewCustomerModal
            onClose={() => setShowNewModal(false)}
            onCustomerCreated={fetchCustomers}
          />
        )}
        {showEditModal && editingCustomer && (
          <EditCustomerModal
            customer={editingCustomer}
            onClose={() => setShowEditModal(false)}
            onCustomerUpdated={fetchCustomers}
          />
        )}
      </div>
    </div>
  );
}
